import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Building2, GripVertical } from "lucide-react";
import { api, BASE } from "../../services/api";
import ImageUpload from "../../components/ui/ImageUpload";

function resolveImg(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

// ── Shared helpers ────────────────────────────────────────────────────────────
function Field({ label, value, onChange, placeholder }) {
    return (
        <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</label>
            <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
                className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60" />
        </div>
    );
}

function DeleteConfirm({ onClose, onConfirm, label = "Táto akcia je nevratná." }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative bg-nova-surface border border-white/10 rounded-xl p-6 max-w-sm w-full z-10">
                <h2 className="font-heading font-bold text-white mb-2">Odstrániť?</h2>
                <p className="text-zinc-400 text-sm mb-5">{label}</p>
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 h-10 rounded-md border border-white/15 text-zinc-300 text-sm hover:bg-white/5">Zrušiť</button>
                    <button onClick={onConfirm} className="flex-1 h-10 rounded-md bg-red-500 text-white font-semibold text-sm hover:bg-red-600">Odstrániť</button>
                </div>
            </div>
        </div>
    );
}

// ── Testimonials tab ──────────────────────────────────────────────────────────
const T_EMPTY = { quote: "", author: "", role: "", event: "", company_logo: "" };

function TestimonialModal({ item, onClose, onSave }) {
    const [form, setForm] = useState(item || T_EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState("");
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.quote || !form.author) { setError("Vyplňte citát a autora."); return; }
        setSaving(true);
        try { await onSave(form); }
        catch (err) { setError(err.message); setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative bg-nova-surface border border-white/10 rounded-xl w-full max-w-lg p-6 z-10 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-heading font-bold text-white">{item ? "Upraviť hodnotenie" : "Nové hodnotenie"}</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Citát</label>
                        <textarea value={form.quote} onChange={e => set("quote", e.target.value)} rows={3}
                            placeholder="Perfektný servis od A po Z…"
                            className="w-full px-3 py-2 rounded-md bg-nova-black border border-white/10 text-white text-sm resize-none focus:outline-none focus:border-nova-cyan/60" />
                    </div>
                    <Field label="Autor" value={form.author} onChange={v => set("author", v)} placeholder="Marta Horváthová" />
                    <Field label="Pozícia / spoločnosť" value={form.role} onChange={v => set("role", v)} placeholder="Event manager, Tatra Group" />
                    <Field label="Udalosť" value={form.event} onChange={v => set("event", v)} placeholder="Firemná konferencia" />
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                            Logo spoločnosti <span className="normal-case text-zinc-700 font-sans font-normal">(nepovinné)</span>
                        </label>
                        <ImageUpload value={form.company_logo || ""} onChange={v => set("company_logo", v)} />
                        {form.company_logo && (
                            <div className="mt-3 flex items-center gap-3 px-3 py-2 rounded-lg bg-nova-black/40 border border-white/8">
                                <img src={resolveImg(form.company_logo)} alt="Logo" className="h-7 w-auto max-w-[120px] object-contain opacity-80" />
                                <button type="button" onClick={() => set("company_logo", "")} className="ml-auto text-zinc-600 hover:text-red-400 transition-colors">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                    {error && <p className="text-red-400 text-xs font-mono">{error}</p>}
                    <div className="flex gap-3 pt-1">
                        <button type="button" onClick={onClose} className="flex-1 h-10 rounded-md border border-white/15 text-zinc-300 text-sm hover:bg-white/5 transition-colors">Zrušiť</button>
                        <button type="submit" disabled={saving} className="flex-1 h-10 rounded-md bg-nova-cyan text-black font-semibold text-sm disabled:opacity-60">
                            {saving ? "Ukladám…" : "Uložiť"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function TestimonialsTab() {
    const [items, setItems]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [modal, setModal]       = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = () => api.getTestimonials().then(setItems).finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        if (modal === "new") await api.createTestimonial(form);
        else await api.updateTestimonial(modal.id, form);
        setModal(null);
        load();
    };

    return (
        <>
            <div className="flex justify-end mb-4">
                <button onClick={() => setModal("new")}
                    className="flex items-center gap-2 h-9 px-4 bg-nova-cyan text-black rounded-md text-sm font-semibold hover:saturate-125 transition-all">
                    <Plus className="w-4 h-4" /> Pridať
                </button>
            </div>
            {loading ? (
                <div className="text-zinc-500 text-sm font-mono">Načítanie…</div>
            ) : (
                <div className="space-y-3">
                    {items.map(item => (
                        <div key={item.id} className="border border-white/8 bg-nova-surface/30 rounded-xl p-5 hover:border-white/15 transition-colors">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <p className="text-white text-sm leading-relaxed mb-3">"{item.quote}"</p>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        {item.company_logo
                                            ? <img src={resolveImg(item.company_logo)} alt="logo" className="h-5 w-auto max-w-[80px] object-contain opacity-70" />
                                            : <Building2 className="w-3.5 h-3.5 text-zinc-700" />
                                        }
                                        <span className="font-medium text-white text-xs">{item.author}</span>
                                        <span className="text-zinc-500 text-xs">{item.role}</span>
                                        <span className="font-mono text-[9px] uppercase tracking-wider text-nova-cyan border border-nova-cyan/20 px-2 py-0.5 rounded-sm">{item.event}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => setModal(item)} className="p-1.5 text-zinc-500 hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => setDeleting(item.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && (
                        <div className="border border-white/8 rounded-xl py-12 text-center text-zinc-600 text-sm">Žiadne hodnotenia. Pridajte prvé.</div>
                    )}
                </div>
            )}
            {modal && <TestimonialModal item={modal === "new" ? null : modal} onClose={() => setModal(null)} onSave={save} />}
            {deleting && <DeleteConfirm onClose={() => setDeleting(null)} onConfirm={async () => { await api.deleteTestimonial(deleting); setDeleting(null); load(); }} />}
        </>
    );
}

// ── Partners tab ──────────────────────────────────────────────────────────────
const P_EMPTY = { name: "", logo_url: "", order: 99 };

function PartnerModal({ item, onClose, onSave }) {
    const [form, setForm] = useState(item || P_EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState("");
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.logo_url) { setError("Nahrajte logo."); return; }
        setSaving(true);
        try { await onSave(form); }
        catch (err) { setError(err.message); setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative bg-nova-surface border border-white/10 rounded-xl w-full max-w-md p-6 z-10">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-heading font-bold text-white">{item ? "Upraviť partnera" : "Nový partner"}</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    <Field label="Názov spoločnosti" value={form.name} onChange={v => set("name", v)} placeholder="T-Mobile, Lidl, Deutsche Telekom…" />
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Logo</label>
                        <ImageUpload value={form.logo_url || ""} onChange={v => set("logo_url", v)} />
                        {form.logo_url && (
                            <div className="mt-3 flex items-center gap-3 px-3 py-2 rounded-lg bg-nova-black/40 border border-white/8">
                                <img src={resolveImg(form.logo_url)} alt="Logo" className="h-8 w-auto max-w-[160px] object-contain opacity-90" />
                                <button type="button" onClick={() => set("logo_url", "")} className="ml-auto text-zinc-600 hover:text-red-400 transition-colors">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Poradie</label>
                        <input type="number" value={form.order} onChange={e => set("order", parseInt(e.target.value) || 99)} min={1}
                            className="w-24 h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60" />
                    </div>
                    {error && <p className="text-red-400 text-xs font-mono">{error}</p>}
                    <div className="flex gap-3 pt-1">
                        <button type="button" onClick={onClose} className="flex-1 h-10 rounded-md border border-white/15 text-zinc-300 text-sm hover:bg-white/5 transition-colors">Zrušiť</button>
                        <button type="submit" disabled={saving} className="flex-1 h-10 rounded-md bg-nova-cyan text-black font-semibold text-sm disabled:opacity-60">
                            {saving ? "Ukladám…" : "Uložiť"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function PartnersTab() {
    const [items, setItems]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [modal, setModal]       = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = () => api.getPartners().then(setItems).finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        if (modal === "new") await api.createPartner(form);
        else await api.updatePartner(modal.id, form);
        setModal(null);
        load();
    };

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <p className="text-zinc-500 text-xs font-mono">Logá sa zobrazia ako partnerský pruh pod hodnoteniami.</p>
                <button onClick={() => setModal("new")}
                    className="flex items-center gap-2 h-9 px-4 bg-nova-cyan text-black rounded-md text-sm font-semibold hover:saturate-125 transition-all">
                    <Plus className="w-4 h-4" /> Pridať logo
                </button>
            </div>
            {loading ? (
                <div className="text-zinc-500 text-sm font-mono">Načítanie…</div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {items.map(item => (
                        <div key={item.id} className="border border-white/8 bg-nova-surface/30 rounded-xl p-4 hover:border-white/15 transition-colors group">
                            <div className="flex items-center justify-center h-14 mb-3">
                                {item.logo_url
                                    ? <img src={resolveImg(item.logo_url)} alt={item.name} className="max-h-12 max-w-full object-contain opacity-80" />
                                    : <Building2 className="w-8 h-8 text-zinc-700" />
                                }
                            </div>
                            <p className="text-zinc-400 text-xs text-center truncate mb-3">{item.name || "—"}</p>
                            <div className="flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => setModal(item)} className="p-1.5 text-zinc-500 hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                                <button onClick={() => setDeleting(item.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && (
                        <div className="col-span-full border border-white/8 rounded-xl py-12 text-center text-zinc-600 text-sm">
                            Žiadni partneri. Pridajte prvé logo.
                        </div>
                    )}
                </div>
            )}
            {modal && <PartnerModal item={modal === "new" ? null : modal} onClose={() => setModal(null)} onSave={save} />}
            {deleting && <DeleteConfirm onClose={() => setDeleting(null)} onConfirm={async () => { await api.deletePartner(deleting); setDeleting(null); load(); }} />}
        </>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminTestimonials() {
    const [tab, setTab] = useState("hodnotenia");

    return (
        <div>
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-1">/ Hodnotenia & Partneri</div>
                <h1 className="font-heading text-2xl font-black tracking-tight">Správa hodnotení</h1>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 border-b border-white/8">
                {[{ val: "hodnotenia", label: "Hodnotenia" }, { val: "partneri", label: "Partneri / Logá" }].map(t => (
                    <button
                        key={t.val}
                        onClick={() => setTab(t.val)}
                        className={`px-5 py-2.5 text-sm font-mono uppercase tracking-wide border-b-2 -mb-px transition-colors ${
                            tab === t.val
                                ? "border-nova-cyan text-nova-cyan"
                                : "border-transparent text-zinc-500 hover:text-zinc-300"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "hodnotenia" ? <TestimonialsTab /> : <PartnersTab />}
        </div>
    );
}
